/**
 * COMMERCE-R5.5 / R5.7 — compact mobile PDF viewer with devicePixelRatio rendering.
 * R5.7: thumb-friendly bottom Back; title-only top (no competing Back).
 */
import { useEffect, useRef, useState, type CSSProperties } from "react";
import { renderPdfPagesHighDpi } from "./DocumentNativeReader.js";

export type DocumentPdfViewerProps = {
  httpsUrl: string;
  titleUk: string;
  documentNumber?: string | null;
  lang: "uk" | "en" | "ua";
  onBack: () => void;
  onDownload: () => void;
  onOpenExternal: () => void;
  onCopyNumber?: () => void;
};

export function DocumentPdfViewerV2(props: DocumentPdfViewerProps) {
  const en = props.lang === "en";
  const hostRef = useRef<HTMLDivElement | null>(null);
  const [pageCount, setPageCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const host = hostRef.current;
        if (!host) return;
        const pages = await renderPdfPagesHighDpi({
          httpsUrl: props.httpsUrl,
          host,
          minCssWidth: 300,
          testIdPrefix: "sv2-pdf-page",
        });
        if (cancelled) return;
        setPageCount(pages);
        setLoading(false);
      } catch {
        if (!cancelled) {
          setError(
            en
              ? "Could not render the document in the app."
              : "Не вдалося відобразити документ у застосунку.",
          );
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [props.httpsUrl, en]);

  const shell: CSSProperties = {
    position: "fixed",
    inset: 0,
    zIndex: 80,
    background: "#eceae6",
    display: "flex",
    flexDirection: "column",
  };

  const title =
    props.documentNumber != null && String(props.documentNumber).trim()
      ? `${props.titleUk} № ${String(props.documentNumber).trim()}`
      : props.titleUk;

  const btnPrimary: CSSProperties = {
    flex: 1,
    minHeight: 48,
    borderRadius: 12,
    border: "none",
    background: "var(--sv2-btn, #1f3d2a)",
    color: "var(--sv2-btn-text, #ffffff)",
    fontWeight: 800,
    fontSize: 15,
    cursor: "pointer",
  };
  const btnSecondary: CSSProperties = {
    minHeight: 48,
    minWidth: 48,
    borderRadius: 12,
    border: "1.5px solid var(--sv2-btn, #1f3d2a)",
    background: "#ffffff",
    color: "var(--sv2-btn, #1f3d2a)",
    fontWeight: 700,
    fontSize: 15,
    cursor: "pointer",
    padding: "0 14px",
  };

  return (
    <div style={shell} data-testid="sv2-document-pdf-viewer" data-reader-mode="pdf-v3">
      <header
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          padding: "8px 10px",
          borderBottom: "1px solid rgba(0,0,0,0.08)",
          background: "#fff",
          minHeight: 44,
        }}
      >
        <div
          style={{
            flex: 1,
            minWidth: 0,
            fontWeight: 600,
            fontSize: 15,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
          data-testid="sv2-pdf-title"
        >
          {title}
        </div>
        <button
          type="button"
          style={{ ...btnSecondary, minWidth: 44 }}
          data-testid="sv2-pdf-more"
          onClick={() => setMenuOpen((v) => !v)}
          aria-label="More"
        >
          ⋯
        </button>
      </header>

      <main style={{ flex: 1, overflow: "auto", padding: "6px 8px 88px" }}>
        {loading ? (
          <p className="sv2-muted" data-testid="sv2-pdf-loading">
            {en ? "Loading document…" : "Завантаження документа…"}
          </p>
        ) : null}
        {error ? (
          <div data-testid="sv2-pdf-error">
            <p style={{ marginTop: 0 }}>{error}</p>
            <button type="button" className="sv2-cta sv2-secondary" onClick={props.onDownload}>
              {en ? "Download PDF" : "Завантажити PDF"}
            </button>
          </div>
        ) : (
          <div ref={hostRef} data-testid="sv2-pdf-pages" />
        )}
        {!loading && !error && pageCount > 0 ? (
          <p className="sv2-muted" style={{ fontSize: 11, textAlign: "center" }} data-testid="sv2-pdf-page-count">
            {en ? `Pages: ${pageCount}` : `Сторінок: ${pageCount}`}
          </p>
        ) : null}
      </main>

      <footer
        style={{
          position: "sticky",
          bottom: 0,
          display: "flex",
          gap: 8,
          padding: "8px 10px calc(8px + env(safe-area-inset-bottom, 0px))",
          borderTop: "1px solid rgba(0,0,0,0.08)",
          background: "#fff",
          zIndex: 2,
        }}
        data-testid="sv2-pdf-bottom-nav"
      >
        <button type="button" style={btnPrimary} data-testid="sv2-pdf-back" onClick={props.onBack}>
          ← {en ? "Back" : "Назад"}
        </button>
        <button type="button" style={btnSecondary} data-testid="sv2-pdf-download" onClick={props.onDownload}>
          {en ? "Download" : "Завантажити"}
        </button>
      </footer>
      {menuOpen ? (
        <div
          style={{
            position: "absolute",
            right: 10,
            top: 48,
            background: "#fff",
            border: "1px solid rgba(0,0,0,0.12)",
            borderRadius: 8,
            padding: 8,
            zIndex: 81,
            minWidth: 200,
          }}
          data-testid="sv2-pdf-overflow"
        >
          <button
            type="button"
            className="sv2-about-link"
            style={{ display: "block", width: "100%", textAlign: "left", marginBottom: 6 }}
            onClick={() => {
              setMenuOpen(false);
              props.onDownload();
            }}
          >
            {en ? "Download PDF" : "Завантажити PDF"}
          </button>
          <button
            type="button"
            className="sv2-about-link"
            style={{ display: "block", width: "100%", textAlign: "left", marginBottom: 6 }}
            onClick={() => {
              setMenuOpen(false);
              props.onCopyNumber?.();
            }}
          >
            {en ? "Copy number" : "Копіювати номер"}
          </button>
          <button
            type="button"
            className="sv2-about-link"
            style={{ display: "block", width: "100%", textAlign: "left" }}
            onClick={() => {
              setMenuOpen(false);
              props.onOpenExternal();
            }}
          >
            {en ? "Open in browser (fallback)" : "Відкрити у браузері (запасний)"}
          </button>
        </div>
      ) : null}
    </div>
  );
}
